import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { HealthScoreBreakdown } from '@/types/supremeIntelligenceTypes';

/**
 * Format currency values for PDF display
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format percentage values for PDF display
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

/**
 * Generate executive summary text
 */
export const generateExecutiveSummary = (
  formData: EnhancedUnifiedFormData,
  healthScore?: HealthScoreBreakdown
): string => {
  const homePrice = formatCurrency(formData.propertyLoan.homePrice);
  const downPayment = formatCurrency(formData.propertyLoan.downPaymentAmount);
  const creditScore = formData.personalDetails.creditScore;
  
  let summary = `Property Analysis for ${homePrice} home with ${downPayment} down payment. `;
  summary += `Applicant credit score: ${creditScore}. `;
  
  if (healthScore) {
    summary += `Financial Health Score: ${healthScore.totalScore}/100 (${healthScore.grade}). `;
    summary += `Key strengths: ${healthScore.strengths.join(', ')}. `;
    
    if (healthScore.improvements.length > 0) {
      summary += `Areas for improvement: ${healthScore.improvements.join(', ')}.`;
    }
  }
  
  return summary;
};

/**
 * Format budget impact data for PDF
 */
export const formatBudgetImpact = (budgetData: any): string[] => {
  if (!budgetData) return ['Budget impact data not available'];
  
  return [
    `Monthly Income: ${formatCurrency(budgetData.monthlyIncome)}`,
    `Total Monthly Debt: ${formatCurrency(budgetData.totalMonthlyDebt)}`,
    `Disposable Income (Before): ${formatCurrency(budgetData.disposableIncomeBefore)}`,
    `Disposable Income (After): ${formatCurrency(budgetData.disposableIncomeAfter)}`,
    `Savings Rate: ${formatPercentage(budgetData.savingsRate)}`,
    `Emergency Fund: ${budgetData.emergencyFundMonths.toFixed(1)} months`,
  ];
};

/**
 * Format loan program recommendations for PDF
 */
export const formatLoanPrograms = (programs: any[]): string[] => {
  if (!programs || programs.length === 0) return ['No loan programs available'];
  
  return programs.slice(0, 3).map((program, index) => {
    return `${index + 1}. ${program.name}: ${formatCurrency(program.monthlyPayment)}/month, ` +
           `${formatPercentage(program.interestRate)} APR, Score: ${program.score}/100`;
  });
};

/**
 * Format credit score actions for PDF
 */
export const formatCreditActions = (actions: any[]): string[] => {
  if (!actions || actions.length === 0) return ['No credit improvement actions available'];
  
  return actions.map((action, index) => {
    return `${index + 1}. ${action.name}: ${action.description} (Impact: +${action.impact} points in ${action.timeToImpact})`;
  });
};

/**
 * Format savings goals for PDF
 */
export const formatSavingsGoals = (savingsData: any): string[] => {
  if (!savingsData) return ['Savings goal data not available'];
  
  return [
    `Down Payment Goal: ${formatCurrency(savingsData.downPaymentGoal)}`,
    `Current Savings: ${formatCurrency(savingsData.currentSavings || 0)}`,
    `Monthly Savings Needed: ${formatCurrency(savingsData.monthlySavingsNeeded || 0)}`,
    `Time to Goal: ${savingsData.monthsToGoal || 'TBD'} months`,
  ];
};
